import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface TopNavProps {
  cartCount: number;
  onCartClick: () => void;
  onLoginClick: () => void;
}

const navCategories = [
  "手机数码", "家用电器", "美妆护肤", "服装鞋包",
  "运动户外", "食品生鲜", "图书文具", "母婴玩具", "汽车用品", "奢侈品",
];

const mobileQuickLinks = [
  { icon: "ri-shopping-bag-line", label: "我的订单", path: "/buyer-confirm", color: "text-green-600" },
  { icon: "ri-message-3-line", label: "Crowntalk", path: "/crowntalk", color: "text-amber-600" },
  { icon: "ri-building-line", label: "工厂后台", path: "/factory-dashboard", color: "text-emerald-600" },
  { icon: "ri-medal-line", label: "分销商中心", path: "/reseller-dashboard", color: "text-orange-500" },
  { icon: "ri-vip-crown-line", label: "达人佣金", path: "/influencer-dashboard", color: "text-amber-500" },
  { icon: "ri-book-open-line", label: "教育书籍", path: "/books", color: "text-teal-600" },
  { icon: "ri-heart-line", label: "爱心捐赠", path: "/donation", color: "text-pink-500" },
  { icon: "ri-user-line", label: "我的账户", action: "login", color: "text-red-500" },
];

export default function TopNav({ cartCount, onCartClick, onLoginClick }: TopNavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu on route change
  const handleNav = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <>
      {/* Top bar - desktop only */}
      <div className="bg-red-600 text-white text-xs py-1.5 px-4 hidden md:block">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span>欢迎来到皇冠鸿运商城！</span>
            <a href="#" className="hover:underline cursor-pointer">请登录</a>
            <span className="text-red-300">|</span>
            <a href="#" className="hover:underline cursor-pointer">免费注册</a>
          </div>
          <div className="flex items-center gap-4">
            <a href="#" className="hover:underline cursor-pointer flex items-center gap-1">
              <i className="ri-map-pin-line"></i> 北京
            </a>
            <button onClick={() => navigate("/buyer-confirm")} className="hover:underline cursor-pointer flex items-center gap-1 whitespace-nowrap">
              <i className="ri-shopping-bag-line"></i> 我的订单
            </button>
            <a href="#" className="hover:underline cursor-pointer">客户服务</a>
            <span className="text-red-300">|</span>
            <button onClick={() => navigate("/factory-dashboard")} className="hover:underline cursor-pointer flex items-center gap-1 text-green-300 font-medium whitespace-nowrap">
              <i className="ri-building-line"></i> 工厂后台
            </button>
            <span className="text-red-300">|</span>
            <button onClick={() => navigate("/reseller-dashboard")} className="hover:underline cursor-pointer flex items-center gap-1 text-yellow-300 font-medium whitespace-nowrap">
              <i className="ri-medal-line"></i> 分销商中心
            </button>
            <span className="text-red-300">|</span>
            <button onClick={() => navigate("/influencer-dashboard")} className="hover:underline cursor-pointer flex items-center gap-1 font-medium whitespace-nowrap text-amber-200">
              <i className="ri-vip-crown-line"></i> 达人佣金中心
            </button>
            <span className="text-red-300">|</span>
            <button onClick={() => navigate("/books")} className="hover:underline cursor-pointer flex items-center gap-1 font-medium whitespace-nowrap text-green-200">
              <i className="ri-book-open-line"></i> 教育书籍
            </button>
            <span className="text-red-300">|</span>
            <button onClick={() => navigate("/donation")} className="hover:underline cursor-pointer flex items-center gap-1 font-medium whitespace-nowrap text-pink-200">
              <i className="ri-heart-line"></i> 爱心捐赠
            </button>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-sm" : "bg-white"}`}>
        <div className="max-w-7xl mx-auto px-3 md:px-4 py-2.5 md:py-3 flex items-center gap-2 md:gap-4">
          {/* Logo */}
          <a href="/" className="flex items-center gap-1.5 flex-shrink-0 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 rounded-xl px-2.5 py-1.5 hover:from-amber-500 hover:to-amber-600 transition-all">
            <img
              src="https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png"
              alt="皇冠鸿运商城 Logo"
              className="h-8 md:h-9 w-auto object-contain"
            />
            <span className="text-gray-900 font-bold text-base md:text-xl hidden sm:block whitespace-nowrap" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              皇冠鸿运商城
            </span>
          </a>

          {/* Search bar */}
          <div className="flex-1">
            <div className="flex items-center border-2 border-red-500 rounded-md overflow-hidden">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索商品..."
                className="flex-1 px-3 py-2 text-sm outline-none bg-white min-w-0"
              />
              <button className="bg-red-500 hover:bg-red-600 text-white px-3 md:px-5 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex-shrink-0">
                <i className="ri-search-line md:mr-1"></i><span className="hidden md:inline">搜索</span>
              </button>
            </div>
            <div className="hidden md:flex items-center gap-3 mt-1 flex-wrap">
              {["iPhone 15", "戴森吸尘器", "SK-II神仙水", "耐克Air Jordan", "华为Mate60"].map((tag) => (
                <button key={tag} className="text-xs text-gray-400 hover:text-red-500 cursor-pointer transition-colors whitespace-nowrap">
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1.5 md:gap-3 flex-shrink-0">
            <button
              onClick={onLoginClick}
              className="hidden md:flex flex-col items-center gap-0.5 cursor-pointer hover:text-red-500 transition-colors text-gray-600"
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-user-line text-xl"></i>
              </div>
              <span className="text-xs whitespace-nowrap">我的账户</span>
            </button>
            <button
              onClick={onCartClick}
              className="flex flex-col items-center gap-0.5 cursor-pointer hover:text-red-500 transition-colors text-gray-600 relative"
            >
              <div className="w-8 h-8 flex items-center justify-center relative">
                <i className="ri-shopping-cart-line text-xl"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </div>
              <span className="text-xs whitespace-nowrap hidden md:block">购物车</span>
            </button>
            <button
              className="md:hidden flex items-center justify-center w-9 h-9 cursor-pointer text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="菜单"
            >
              <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {/* Category nav - desktop only */}
        <div className="border-t border-gray-100 hidden md:block">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-0">
              <button className="flex items-center gap-1.5 px-4 py-2.5 bg-red-500 text-white text-sm font-medium cursor-pointer whitespace-nowrap">
                <i className="ri-menu-line"></i> 全部分类
              </button>
              {navCategories.map((cat) => (
                <button
                  key={cat}
                  className="px-3 py-2.5 text-sm text-gray-600 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                  onClick={() => navigate(`/${cat}`)}
                >
                  {cat}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-1">
                <button className="px-3 py-2.5 text-sm text-green-600 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1 hover:bg-green-50 transition-colors" onClick={() => navigate("/buyer-confirm")}>
                  <i className="ri-shopping-bag-line"></i> 我的订单
                </button>
                <button className="px-3 py-2.5 text-sm text-emerald-600 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1 hover:bg-emerald-50 transition-colors" onClick={() => navigate("/factory-dashboard")}>
                  <i className="ri-building-line"></i> 工厂后台
                </button>
                <button className="px-3 py-2.5 text-sm text-amber-600 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1 hover:bg-amber-50 transition-colors" onClick={() => navigate("/reseller-dashboard")}>
                  <i className="ri-medal-line"></i> 分销商中心
                </button>
                <button className="px-3 py-1.5 text-sm font-bold cursor-pointer whitespace-nowrap flex items-center gap-1.5 transition-all bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-gray-900 rounded-full" onClick={() => navigate("/crowntalk")}>
                  <i className="ri-message-3-line"></i> Crowntalk
                </button>
                <button className="px-3 py-1.5 text-sm font-bold cursor-pointer whitespace-nowrap flex items-center gap-1.5 transition-all bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-white rounded-full" onClick={() => navigate("/influencer-dashboard")}>
                  <i className="ri-vip-crown-line"></i> 达人入口
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu - full featured */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 overflow-y-auto max-h-[80vh]">
            {/* Quick links grid */}
            <div className="px-4 pt-4 pb-2">
              <p className="text-xs text-gray-400 font-medium mb-3">快捷入口</p>
              <div className="grid grid-cols-4 gap-2">
                {mobileQuickLinks.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => {
                      if (link.action === "login") { setMobileMenuOpen(false); onLoginClick(); }
                      else handleNav(link.path!);
                    }}
                    className="flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 ${link.color}`}>
                      <i className={`${link.icon} text-lg`}></i>
                    </div>
                    <span className="text-xs text-gray-600 text-center leading-tight">{link.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="mx-4 border-t border-gray-100 my-2"></div>

            {/* Categories */}
            <div className="px-4 pb-4">
              <p className="text-xs text-gray-400 font-medium mb-3">商品分类</p>
              <div className="grid grid-cols-4 gap-2">
                {navCategories.map((cat) => (
                  <button
                    key={cat}
                    className="text-xs text-gray-600 py-2.5 px-1 text-center hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer transition-colors"
                    onClick={() => handleNav(`/${cat}`)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Crowntalk CTA */}
            <div className="mx-4 mb-4">
              <button
                onClick={() => handleNav("/crowntalk")}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 font-bold py-3 rounded-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-message-3-line"></i> 下载 Crowntalk 全球社交应用
              </button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
